if (!customElements.get('countdown-stats')) {
  class CountdownStats extends HTMLElement {
    constructor() {
      super();
      this.items = this.querySelectorAll('.countdown-stats__item');
      this.hasAnimated = false;
      this.observer = null;
    }

    connectedCallback() {
      // Check if user prefers reduced motion
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      if (prefersReducedMotion) {
        // Skip animation and show final values immediately
        this.showFinalValues();
        return;
      }

      // Use Intersection Observer to trigger animation when section is visible
      this.setupIntersectionObserver();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.2 // Trigger when 20% of the section is visible
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.animateCounters();
            this.hasAnimated = true;
            // Disconnect observer after animation starts
            this.observer.disconnect();
          }
        });
      }, options);

      this.observer.observe(this);
    }

    detectDelimiter(value) {
      // Check what delimiter is used in the original value
      const stringValue = value.toString();
      
      if (stringValue.includes(' ')) {
        return 'space';
      } else if (stringValue.includes('.')) {
        return 'dot';
      } else if (stringValue.includes(',')) {
        return 'comma';
      }
      
      // Default to comma for numbers without delimiters
      return 'comma';
    }

    showFinalValues() {
      this.items.forEach(item => {
        const originalValue = item.dataset.value;
        const delimiter = this.detectDelimiter(originalValue);
        const numericValue = this.parseNumericValue(originalValue);
        const valueElement = item.querySelector('.countdown-stats__value');
        
        valueElement.textContent = this.formatNumber(numericValue, delimiter);
      });
    }

    parseNumericValue(value) {
      // Remove all delimiters (spaces, commas, dots) and parse as float
      const cleaned = value.toString().replace(/[\s,\.]/g, '');
      return parseFloat(cleaned);
    }

    animateCounters() {
      this.items.forEach(item => {
        const originalValue = item.dataset.value;
        const delimiter = this.detectDelimiter(originalValue);
        const numericValue = this.parseNumericValue(originalValue);
        const duration = parseInt(item.dataset.duration) || 2000;
        const valueElement = item.querySelector('.countdown-stats__value');
        
        this.animateValue(valueElement, 0, numericValue, duration, delimiter);
      });
    }

    animateValue(element, start, end, duration, delimiter) {
      const startTime = performance.now();
      
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth animation (easeOutExpo)
        const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
        
        const currentValue = start + (end - start) * easeProgress;
        
        // Format the number with the appropriate delimiter
        element.textContent = this.formatNumber(currentValue, delimiter);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Ensure final value is exact
          element.textContent = this.formatNumber(end, delimiter);
        }
      };
      
      requestAnimationFrame(animate);
    }

    formatNumber(value, delimiter = 'comma') {
      // Round to nearest integer
      const rounded = Math.round(value);
      
      // For numbers less than 1000, no delimiter needed
      if (rounded < 1000) {
        return rounded.toString();
      }
      
      // Convert to string and add delimiters
      const numStr = rounded.toString();
      const parts = [];
      
      // Split into groups of 3 from right to left
      for (let i = numStr.length; i > 0; i -= 3) {
        const start = Math.max(0, i - 3);
        parts.unshift(numStr.slice(start, i));
      }
      
      // Join with appropriate delimiter
      switch (delimiter) {
        case 'space':
          return parts.join(' ');
        case 'dot':
          return parts.join('.');
        case 'comma':
        default:
          return parts.join(',');
      }
    }
  }

  customElements.define('countdown-stats', CountdownStats);
}
